import { useEffect, useRef, useState } from 'react';

import { getPageHeightInPx } from '../utils/page-utils';

/** Matches backend Puppeteer PDF viewport width (cv.service.ts). */
export const PDF_VIEWPORT_WIDTH = 600;

export const VIEWPORT_BREAKPOINT = 601;

export interface UseCVPreviewScaleOptions {
  /** When this changes, spacer is recomputed (e.g. stringified cv/params). */
  contentKey?: string | number;
}

export interface UseCVPreviewScaleResult {
  isNarrowViewport: boolean;
  scale: number;
  lastPageSpacerHeight: number;
  contentHeightWithoutSpacer: number | null;
  contentLogicalHeightPx: number;
  pdfViewportRef: React.RefObject<HTMLDivElement | null>;
  cvContentRef: React.RefObject<HTMLDivElement | null>;
  wrapperStyle: React.CSSProperties | undefined;
  viewportClassName: string;
  viewportStyle: React.CSSProperties;
}

export function useCVPreviewScale(options: UseCVPreviewScaleOptions = {}): UseCVPreviewScaleResult {
  const { contentKey } = options;
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024,
  );
  const [lastPageSpacerHeight, setLastPageSpacerHeight] = useState(0);
  const [contentHeightWithoutSpacer, setContentHeightWithoutSpacer] = useState<number | null>(null);
  const [contentLogicalHeightPx, setContentLogicalHeightPx] = useState(0);
  const pdfViewportRef = useRef<HTMLDivElement>(null);
  const cvContentRef = useRef<HTMLDivElement>(null);

  const isNarrowViewport = viewportWidth < VIEWPORT_BREAKPOINT;
  const scale = isNarrowViewport ? viewportWidth / PDF_VIEWPORT_WIDTH : 1;

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!isNarrowViewport || !cvContentRef.current) {
      setLastPageSpacerHeight(0);
      setContentHeightWithoutSpacer(null);
      setContentLogicalHeightPx(0);
      return undefined;
    }
    const el = cvContentRef.current;
    const pageHeightPx = getPageHeightInPx();
    const updateSpacer = () => {
      const contentHeight = el.scrollHeight;
      setContentHeightWithoutSpacer(contentHeight);
      const remainder = contentHeight % pageHeightPx;
      const spacer = remainder === 0 ? 0 : (pageHeightPx - remainder) % pageHeightPx;
      setLastPageSpacerHeight(spacer);
    };
    const ro = new ResizeObserver(updateSpacer);
    ro.observe(el);
    const t = setTimeout(updateSpacer, 150);
    return () => {
      clearTimeout(t);
      ro.disconnect();
    };
  }, [isNarrowViewport, contentKey]);

  useEffect(() => {
    if (!isNarrowViewport || !pdfViewportRef.current) {
      setContentLogicalHeightPx(0);
      return undefined;
    }
    const el = pdfViewportRef.current;
    const update = () => setContentLogicalHeightPx(el.scrollHeight);
    const ro = new ResizeObserver(update);
    ro.observe(el);
    update();
    return () => ro.disconnect();
  }, [isNarrowViewport, lastPageSpacerHeight]);

  const wrapperStyle: React.CSSProperties | undefined =
    contentLogicalHeightPx > 0 ? { height: contentLogicalHeightPx * scale } : undefined;

  const viewportClassName =
    contentLogicalHeightPx > 0 ? 'absolute left-0 top-0 origin-top-left' : 'origin-top-left';

  const viewportStyle: React.CSSProperties = {
    width: PDF_VIEWPORT_WIDTH,
    transform: `scale(${scale})`,
  };

  return {
    isNarrowViewport,
    scale,
    lastPageSpacerHeight,
    contentHeightWithoutSpacer,
    contentLogicalHeightPx,
    pdfViewportRef,
    cvContentRef,
    wrapperStyle,
    viewportClassName,
    viewportStyle,
  };
}
